import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  OBJEKT_MONTAGE_STANDARD, OBJEKT_VERSAND_STANDARD,
  type Objekt, type ObjektGruppe, type ObjektKostenblock, type ObjektMontage, type ObjektMontageModus, type ObjektNaehauftrag, type ObjektPosition, type ObjektVersand,
} from "@repo/shared";

import { Blatt } from "../components/kalkulation/Blatt";
import { Kennzahl } from "../components/kalkulation/BogenRechner";
import { Schalter, ZahlFeld } from "../components/kalkulation/Feld";
import { Segmente } from "../components/kalkulation/Segmente";
import { Zahl } from "../components/kalkulation/Zahl";
import { PositionHinzufuegen, type NeuePosition } from "../components/objekt/PositionHinzufuegen";
import { api, errorMessage } from "../lib/api";
import { cents, euroWert } from "../lib/objekt";
import { useFeder } from "../lib/spring";

type Kopf = { name: string; kunde: string; notiz: string; montage: ObjektMontage; versand: ObjektVersand; provisionProzent: number; sollGewinnProStundeCents: number };

function kopfAus(o: Objekt): Kopf {
  return {
    name: o.name, kunde: o.kunde ?? "", notiz: o.notiz ?? "",
    montage: { ...OBJEKT_MONTAGE_STANDARD, ...o.montage }, versand: { ...OBJEKT_VERSAND_STANDARD, ...o.versand },
    provisionProzent: o.provisionProzent, sollGewinnProStundeCents: o.sollGewinnProStundeCents,
  };
}

const MODI: { id: ObjektMontageModus; label: string }[] = [
  { id: "produkte", label: "Aus Produkten" },
  { id: "tage", label: "Montagetage" },
  { id: "stunden", label: "Stunden" },
];

const istAtelier = (p: ObjektPosition) => p.herstellerSchluessel === "naehatelier";
const lfm = (n: number) => `${n.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} lfm`;

/** Ein Objekt: Positionen aus den Konfiguratoren, Montage-Rahmen, Auswertung mit Einkauf, Verkauf und Gewinn. */
export function ObjektDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [objekt, setObjekt] = useState<Objekt | null>(null);
  const [kopf, setKopf] = useState<Kopf | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [rechnet, setRechnet] = useState(false);
  const [blattOffen, setBlattOffen] = useState(false);
  const [naehauftrag, setNaehauftrag] = useState<ObjektNaehauftrag | null>(null);
  const [naehauftragOffen, setNaehauftragOffen] = useState(false);
  const [naehauftragLaedt, setNaehauftragLaedt] = useState(false);
  const [loeschenBestaetigen, setLoeschenBestaetigen] = useState(false);

  const uebernehmen = useCallback((o: Objekt) => {
    setObjekt(o);
    setKopf(kopfAus(o));
    setNaehauftrag(null);
  }, []);

  useEffect(() => {
    void (async () => {
      const res = await api.api.admin.objekte[":id"].$get({ param: { id } });
      if (!res.ok) {
        setFehler(await errorMessage(res, "Objekt nicht ladbar"));
        setLaedt(false);
        return;
      }
      uebernehmen((await res.json()) as Objekt);
      setLaedt(false);
    })();
  }, [id, uebernehmen]);

  const geaendert = objekt && kopf && JSON.stringify(kopfAus(objekt)) !== JSON.stringify(kopf);

  async function kopfSpeichern() {
    if (!kopf) return;
    setSpeichert(true);
    try {
      const res = await api.api.admin.objekte[":id"].$patch({
        param: { id },
        json: {
          name: kopf.name, kunde: kopf.kunde.trim() || null, notiz: kopf.notiz.trim() || null,
          montage: kopf.montage, versand: kopf.versand, provisionProzent: kopf.provisionProzent, sollGewinnProStundeCents: kopf.sollGewinnProStundeCents,
        },
      });
      if (!res.ok) throw new Error(await errorMessage(res, "Speichern fehlgeschlagen"));
      uebernehmen((await res.json()) as Objekt);
      setFehler(null);
    } catch (cause) {
      setFehler(cause instanceof Error ? cause.message : "Speichern fehlgeschlagen");
    } finally {
      setSpeichert(false);
    }
  }

  async function positionAendern(p: ObjektPosition, patch: PositionPatch) {
    const res = await api.api.admin.objekte[":id"].positionen[":positionId"].$patch({ param: { id, positionId: p.id }, json: patch });
    if (!res.ok) return setFehler(await errorMessage(res, "Position nicht geändert"));
    uebernehmen((await res.json()) as Objekt);
  }

  async function positionEntfernen(p: ObjektPosition) {
    const res = await api.api.admin.objekte[":id"].positionen[":positionId"].$delete({ param: { id, positionId: p.id } });
    if (!res.ok) return setFehler(await errorMessage(res, "Position nicht entfernt"));
    uebernehmen((await res.json()) as Objekt);
  }

  async function hinzufuegen(neu: NeuePosition): Promise<string | null> {
    const res = await api.api.admin.objekte[":id"].positionen.$post({ param: { id }, json: neu });
    if (!res.ok) return errorMessage(res, "Position nicht hinzugefügt");
    uebernehmen((await res.json()) as Objekt);
    return null;
  }

  async function neuBerechnen() {
    setRechnet(true);
    const res = await api.api.admin.objekte[":id"]["neu-berechnen"].$post({ param: { id } });
    setRechnet(false);
    if (!res.ok) return setFehler(await errorMessage(res, "Neu berechnen fehlgeschlagen"));
    uebernehmen((await res.json()) as Objekt);
  }

  async function naehauftragOeffnen() {
    setNaehauftragOffen(true);
    if (naehauftrag) return;
    setNaehauftragLaedt(true);
    const res = await api.api.admin.objekte[":id"].naehauftrag.$get({ param: { id } });
    setNaehauftragLaedt(false);
    if (!res.ok) return setFehler(await errorMessage(res, "Nähauftrag nicht ladbar"));
    setNaehauftrag((await res.json()) as ObjektNaehauftrag);
  }

  async function loeschen() {
    const res = await api.api.admin.objekte[":id"].$delete({ param: { id } });
    if (!res.ok) return setFehler(await errorMessage(res, "Löschen fehlgeschlagen"));
    navigate("/objekte", { replace: true });
  }

  if (laedt) return <p className="muted">Lade Objekt …</p>;
  if (!objekt || !kopf) return <p className="kalk-fehler">{fehler ?? "Objekt nicht gefunden"} · <Link to="/objekte">Zur Liste</Link></p>;

  const a = objekt.auswertung;
  const letzte = objekt.positionen.at(-1);
  const m = kopf.montage;
  const v = kopf.versand;
  const montage = (patch: Partial<ObjektMontage>) => setKopf({ ...kopf, montage: { ...m, ...patch } });
  const hatAtelier = objekt.positionen.some((p) => istAtelier(p) && p.gueltig);

  return (
    <section className="objekt">
      <nav className="breadcrumb"><Link to="/objekte">Objekte</Link> / {objekt.name}</nav>
      <div className="page-head">
        <h1>{objekt.name}</h1>
        <div className="kalk-knoepfe">
          {hatAtelier && (
            <button type="button" className="button" onClick={() => void naehauftragOeffnen()}>Nähauftrag</button>
          )}
          <button type="button" className="button" disabled={rechnet || objekt.positionen.length === 0} onClick={() => void neuBerechnen()}>
            {rechnet ? "Rechnet …" : "Preise neu berechnen"}
          </button>
          <button type="button" className="button kalk-knopf--primaer" onPointerDown={() => setBlattOffen(true)} onClick={() => setBlattOffen(true)}>
            + Position
          </button>
        </div>
      </div>
      {fehler && <p className="kalk-fehler" role="alert">{fehler}</p>}

      {/* --- Kopf, Montage-Rahmen, Versand -------------------------------- */}
      <div className="objekt-kopf">
        <div className="kalk-stoff objekt-karte">
          <h3>Objekt</h3>
          <div className="objekt-karte__felder">
            <label className="kalk-feld"><span className="kalk-feld__label">Name</span><input type="text" value={kopf.name} onChange={(e) => setKopf({ ...kopf, name: e.target.value })} /></label>
            <label className="kalk-feld"><span className="kalk-feld__label">Kunde</span><input type="text" value={kopf.kunde} onChange={(e) => setKopf({ ...kopf, kunde: e.target.value })} /></label>
            <label className="kalk-feld kalk-feld--breit"><span className="kalk-feld__label">Notiz</span><textarea rows={2} value={kopf.notiz} onChange={(e) => setKopf({ ...kopf, notiz: e.target.value })} /></label>
          </div>
          <h3 className="objekt-karte__zwischen">Versand und Ziele</h3>
          <div className="objekt-karte__felder objekt-karte__felder--vier">
            <ZahlFeld label="Pakete" wert={v.pakete} onChange={(n) => setKopf({ ...kopf, versand: { ...v, pakete: Math.max(0, Math.round(n || 0)) } })} min={0} />
            <ZahlFeld label="Paketpreis netto" wert={euroWert(v.paketPreisNettoCents)} onChange={(n) => setKopf({ ...kopf, versand: { ...v, paketPreisNettoCents: cents(n) } })} einheit="€" min={0} schritt={0.5} />
            <ZahlFeld label="Provision" wert={kopf.provisionProzent} onChange={(n) => setKopf({ ...kopf, provisionProzent: n || 0 })} einheit="%" min={0} schritt={0.5} />
            <ZahlFeld label="Soll-Gewinn je h" wert={euroWert(kopf.sollGewinnProStundeCents)} onChange={(n) => setKopf({ ...kopf, sollGewinnProStundeCents: cents(n) })} einheit="€" min={0} schritt={10} />
          </div>
        </div>
        <div className="kalk-stoff objekt-karte">
          <div className="objekt-karte__kopf">
            <h3>Montage</h3>
            <Segmente segmente={MODI.filter((x) => x.id !== "produkte" || objekt.montage.modus === "produkte")} wert={m.modus} onChange={(modus) => montage({ modus: modus as ObjektMontageModus })} />
          </div>
          <p className="kalk-hinweis objekt-karte__hinweis">
            {m.modus === "produkte" && "Stunden aus den gespeicherten Montageminuten der Positionen."}
            {m.modus === "tage" && "Montagetage à 8 Stunden je Monteur, wie im Excel (A)."}
            {m.modus === "stunden" && "Stunden je Monteur direkt angeben, wie im Excel (B)."}
          </p>
          <div className="objekt-karte__felder objekt-karte__felder--vier">
            {m.modus === "tage" && <ZahlFeld label="Montagetage" wert={m.montagetage} onChange={(n) => montage({ montagetage: Math.max(0, n || 0) })} min={0} schritt={0.5} />}
            {m.modus === "stunden" && <ZahlFeld label="Stunden" wert={m.montagestunden} onChange={(n) => montage({ montagestunden: Math.max(0, n || 0) })} einheit="h" min={0} schritt={0.5} />}
            {m.modus !== "produkte" && <ZahlFeld label="Monteure" wert={m.monteure} onChange={(n) => montage({ monteure: Math.max(1, Math.round(n || 1)) })} min={1} />}
            <ZahlFeld label="Lohn netto je h" wert={euroWert(m.nettoLohnProStundeCents)} onChange={(n) => montage({ nettoLohnProStundeCents: cents(n) })} einheit="€" min={0} schritt={0.5} />
            <ZahlFeld label="Marge Lohn" wert={m.margeProzent} onChange={(n) => montage({ margeProzent: n || 0 })} einheit="%" min={0} schritt={5} />
            <ZahlFeld label="Anfahrt brutto" wert={euroWert(m.anfahrtBruttoCents)} onChange={(n) => montage({ anfahrtBruttoCents: cents(n) })} einheit="€" min={0} schritt={5} />
            <ZahlFeld label="Anfahrten" wert={m.anfahrten} onChange={(n) => montage({ anfahrten: Math.max(0, Math.round(n || 0)) })} min={0} />
            <ZahlFeld label="Handling je Dm brutto" wert={euroWert(m.handlingBruttoProDekometerCents)} onChange={(n) => montage({ handlingBruttoProDekometerCents: cents(n) })} einheit="€" min={0} schritt={0.5} />
            <div className="objekt-karte__schalter">
              <Schalter label="Montage in Gruppenpreisen" an={m.inGruppenpreisen} onChange={(an) => montage({ inGruppenpreisen: an })} />
            </div>
          </div>
        </div>
      </div>
      <div className={`objekt-speichern${geaendert ? " objekt-speichern--sichtbar" : ""}`} aria-hidden={!geaendert}>
        <span>Änderungen am Kopf noch nicht gespeichert.</span>
        <button type="button" className="button" onClick={() => objekt && setKopf(kopfAus(objekt))}>Verwerfen</button>
        <button type="button" className="button kalk-knopf--primaer" disabled={speichert || !kopf.name.trim()} onClick={() => void kopfSpeichern()}>
          {speichert ? "Speichert …" : "Speichern"}
        </button>
      </div>

      {/* --- Positionen --------------------------------------------------- */}
      <section className="kalk-abschnitt">
        <h2>Positionen</h2>
        {objekt.positionen.length === 0 ? (
          <div className="callout">
            <strong>Noch keine Position.</strong>
            <p>„+ Position“ öffnet die Konfiguratoren: Hersteller, Produkt, Konfiguration, dann Apartment und Zimmer zuordnen.</p>
          </div>
        ) : (
          <table className="kalk-tabelle objekt-positionen">
            <thead>
              <tr>
                <th>#</th><th>Apartment</th><th>Zimmer</th><th>Position</th><th>Menge</th><th>Dekometer</th>
                <th className="num">EK / Stück</th><th className="num">VK netto / Stück</th><th className="num">Zeile VK netto</th><th className="num">Montage</th><th aria-label="Aktion" />
              </tr>
            </thead>
            <tbody>
              {objekt.positionen.map((p, i) => (
                <PositionZeile key={p.id} p={p} nr={i + 1} modus={m.modus} onAendern={(patch) => void positionAendern(p, patch)} onEntfernen={() => void positionEntfernen(p)} />
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* --- Auswertung --------------------------------------------------- */}
      {objekt.positionen.length > 0 && (
        <>
          <section className="kalk-abschnitt">
            <h2>Auswertung</h2>
            <div className="kalk-kennzahlen">
              {a.bloecke.map((b) => <Block key={b.schluessel} label={`${b.name} · ${b.anzahlPositionen} Pos.`} b={b} />)}
              <Block label={`Montage · ${a.montage.stunden.toLocaleString("de-DE")} h`} b={a.montage} />
              {a.versand.pakete > 0 && <Block label={`Versand · ${a.versand.pakete} Pakete`} b={a.versand} />}
              <Block label="Gesamt" b={a.gesamt} betont />
            </div>
            {a.warnungen.map((w) => <p key={w} className="kalk-warnung">{w}</p>)}
          </section>

          <section className="kalk-abschnitt kalk-auswertung">
            <div className="kalk-auswertung__spalte">
              <h3>Montage</h3>
              <div className="kalk-kennzahlen kalk-kennzahlen--klein">
                <Kennzahl label="Stunden"><Zahl wert={a.montage.stunden} /></Kennzahl>
                <Kennzahl label="Montagetage"><Zahl wert={a.montage.montagetage} /></Kennzahl>
                <Kennzahl label="Anfahrt netto"><Zahl wert={euroWert(a.montage.anfahrtNettoCents)} art="euro" /></Kennzahl>
                <Kennzahl label="Handling netto"><Zahl wert={euroWert(a.montage.handlingNettoCents)} art="euro" /></Kennzahl>
              </div>
              {a.montage.modus === "produkte" && a.montage.fehltFuer.length > 0 && (
                <p className="muted small objekt-hinweis">Montagedauer fehlt für {a.montage.fehltFuer.join(", ")} – Montage nach Tagen bzw. Stunden angeben.</p>
              )}
              {a.gewinnProStunde ? (
                <LohntSich wert={a.gewinnProStunde.quotient} gewinnCents={a.gewinnProStunde.cents} sollCents={a.gewinnProStunde.sollCents} />
              ) : (
                <p className="muted small">Gewinn je Arbeitsstunde erst, wenn Montagestunden vorliegen.</p>
              )}
            </div>
            <div className="kalk-auswertung__spalte">
              <h3>Je Dekometer</h3>
              <div className="kalk-kennzahlen kalk-kennzahlen--klein">
                <Kennzahl label="VK netto"><Zahl wert={euroWert(a.proDekometer.vkNettoCents)} art="euro" /></Kennzahl>
                <Kennzahl label="Gewinn"><Zahl wert={euroWert(a.proDekometer.gewinnNettoCents)} art="euro" /></Kennzahl>
                <Kennzahl label="Dekometer gesamt"><Zahl wert={a.dekometerGesamt} art="m" /></Kennzahl>
                <Kennzahl label="Provision"><Zahl wert={euroWert(a.provisionCents)} art="euro" /></Kennzahl>
              </div>
              <h3 className="objekt-karte__zwischen">Stoffbedarf</h3>
              <div className="kalk-kennzahlen kalk-kennzahlen--klein">
                <Kennzahl label="Oberstoff"><Zahl wert={a.stoffbedarfLfm} art="m" /></Kennzahl>
                <Kennzahl label="Futter"><Zahl wert={a.futterbedarfLfm} art="m" /></Kennzahl>
              </div>
            </div>
            <div className="kalk-auswertung__spalte">
              <h3>Je Apartment</h3>
              <Gruppen gruppen={a.proApartment} />
              <h3 className="objekt-karte__zwischen">Je Zimmer</h3>
              <Gruppen gruppen={a.proZimmer} />
            </div>
          </section>
        </>
      )}

      {/* --- Löschen ------------------------------------------------------ */}
      <section className="kalk-abschnitt objekt-gefahr">
        {loeschenBestaetigen ? (
          <div className="objekt-gefahr__frage">
            <span>Objekt „{objekt.name}“ mit {objekt.positionen.length} Positionen löschen?</span>
            <button type="button" className="button" onClick={() => setLoeschenBestaetigen(false)}>Behalten</button>
            <button type="button" className="button objekt-knopf--gefahr" onClick={() => void loeschen()}>Endgültig löschen</button>
          </div>
        ) : (
          <button type="button" className="button objekt-knopf--leise" onClick={() => setLoeschenBestaetigen(true)}>Objekt löschen …</button>
        )}
      </section>

      <PositionHinzufuegen offen={blattOffen} onClose={() => setBlattOffen(false)} onHinzufuegen={hinzufuegen} vorbelegung={letzte ? { apartment: letzte.apartment, zimmer: letzte.zimmer } : undefined} />

      <Blatt offen={naehauftragOffen} onClose={() => setNaehauftragOffen(false)} titel={`Nähauftrag ${objekt.name}`}>
        {naehauftragLaedt && <p className="muted">Lade Nähauftrag …</p>}
        {naehauftrag && <Naehauftrag n={naehauftrag} />}
      </Blatt>
    </section>
  );
}

type PositionPatch = { apartment?: string | null; zimmer?: string | null; menge?: number; dekometerJeStueck?: number | null };

function PositionZeile({ p, nr, modus, onAendern, onEntfernen }: { p: ObjektPosition; nr: number; modus: ObjektMontageModus; onAendern: (patch: PositionPatch) => void; onEntfernen: () => void }) {
  const [apartment, setApartment] = useState(p.apartment ?? "");
  const [zimmer, setZimmer] = useState(p.zimmer ?? "");
  const [menge, setMenge] = useState(p.menge);
  const jeStueck = Math.round((p.dekometer / p.menge) * 1000) / 1000;
  const [dekometer, setDekometer] = useState(jeStueck);
  useEffect(() => { setApartment(p.apartment ?? ""); setZimmer(p.zimmer ?? ""); setMenge(p.menge); setDekometer(jeStueck); }, [p.apartment, p.zimmer, p.menge, jeStueck]);
  const u = <span className="kalk-unsichtbar" />;
  const stoffbedarf = istAtelier(p) && typeof p.details.stoffbedarfLfm === "number" ? p.details.stoffbedarfLfm * p.menge : null;
  const verknuepft = (p as { verknuepfung?: string | null }).verknuepfung;
  return (
    <tr className={`kalk-zeile${p.gueltig ? "" : " kalk-zeile--fehler"}`}>
      <td className="muted">{nr}</td>
      <td><label className="kalk-feld">{u}<input type="text" value={apartment} placeholder="App." onChange={(e) => setApartment(e.target.value)} onBlur={() => (apartment.trim() || null) !== p.apartment && onAendern({ apartment: apartment.trim() || null })} /></label></td>
      <td><label className="kalk-feld">{u}<input type="text" value={zimmer} placeholder="Zimmer" onChange={(e) => setZimmer(e.target.value)} onBlur={() => (zimmer.trim() || null) !== p.zimmer && onAendern({ zimmer: zimmer.trim() || null })} /></label></td>
      <td>
        <strong>{p.bezeichnung}</strong>
        {verknuepft && <span className="kalk-marke objekt-sus__marke" title={`Verknüpfung ${verknuepft}`}>Stoff &amp; Stange</span>}
        <small className="muted objekt-positionen__meta">
          {p.herstellerName}{p.gueltig ? "" : ` · ${p.fehler ?? "nicht baubar"}`}
          {stoffbedarf != null && ` · Stoff ${lfm(stoffbedarf)}`}
        </small>
      </td>
      <td><label className="kalk-feld">{u}<input type="number" min={1} value={menge} onChange={(e) => setMenge(Math.max(1, Number(e.target.value) || 1))} onBlur={() => menge !== p.menge && onAendern({ menge })} /></label></td>
      <td>
        <span className="objekt-dekometer">
          <label className="kalk-feld">{u}<input type="number" min={0} step={0.1} value={dekometer} onChange={(e) => setDekometer(Number(e.target.value) || 0)} onBlur={() => Math.abs(dekometer - jeStueck) > 1e-6 && onAendern({ dekometerJeStueck: dekometer })} /></label>
          <small className="muted">× {p.menge} = {p.dekometer.toLocaleString("de-DE", { maximumFractionDigits: 2 })} m</small>
          {p.dekometerUeberschrieben && (
            <button type="button" className="kalk-marke objekt-dekometer__reset" title="Wieder aus den Werten ableiten" onClick={() => onAendern({ dekometerJeStueck: null })}>überschrieben ×</button>
          )}
        </span>
      </td>
      <td className="num">{p.preis ? <Zahl wert={euroWert(p.preis.ekNettoCents)} art="euro" /> : "–"}</td>
      <td className="num">{p.preis ? <Zahl wert={euroWert(p.preis.vkNettoCents)} art="euro" /> : "–"}</td>
      <td className="num"><strong>{p.preis ? <Zahl wert={euroWert(p.zeileVkNettoCents)} art="euro" /> : "–"}</strong></td>
      <td className="num">
        {p.montageMinutenJeStueck != null ? `${p.montageMinutenJeStueck * p.menge} min` : modus === "produkte" ? <span className="kalk-marke">fehlt</span> : <span className="muted">–</span>}
      </td>
      <td><button type="button" className="kalk-symbolknopf" aria-label="Position entfernen" onClick={onEntfernen}>×</button></td>
    </tr>
  );
}

function Naehauftrag({ n }: { n: ObjektNaehauftrag }) {
  return (
    <div className="objekt-naehauftrag">
      <div className="objekt-naehauftrag__leiste">
        <span className="muted small">{n.positionen.length} Positionen · Oberstoff {lfm(n.stoffbedarfLfm)}{n.futterbedarfLfm > 0 ? ` · Futter ${lfm(n.futterbedarfLfm)}` : ""}</span>
        <button type="button" className="button kalk-knopf--primaer" onClick={() => window.print()}>Drucken</button>
      </div>
      <dl className="kalk-kopfdaten">
        <div><dt>Objekt</dt><dd>{n.objekt.name}</dd></div>
        <div><dt>Kunde</dt><dd>{n.objekt.kunde ?? "–"}</dd></div>
        <div><dt>Datum</dt><dd>{n.objekt.datum}</dd></div>
        <div><dt>Stoffbedarf</dt><dd>{lfm(n.stoffbedarfLfm)}{n.futterbedarfLfm > 0 ? ` + Futter ${lfm(n.futterbedarfLfm)}` : ""}</dd></div>
      </dl>
      {n.positionen.length === 0 && <p className="muted">Keine gültigen Nähatelier-Positionen.</p>}
      <ol className="objekt-naehauftrag__liste">
        {n.positionen.map((p) => (
          <li key={p.id}>
            <div className="objekt-naehauftrag__kopf">
              <strong>Pos. {p.nr} · {p.bezeichnung}</strong>
              <span className="muted small">{[p.apartment, p.zimmer].filter(Boolean).join(" · ") || "ohne Zuordnung"} · {p.menge}× · {lfm(p.stoffbedarfLfm)}</span>
            </div>
            <ul className="objekt-naehauftrag__zeilen">
              {p.zeilen.map((z, i) => <li key={i}>{z}</li>)}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Block({ label, b, betont }: { label: string; b: ObjektKostenblock; betont?: boolean }) {
  return (
    <div className={`kalk-kennzahl kalk-block${betont ? " kalk-kennzahl--betont" : ""}`}>
      <span>{label}</span>
      <strong><Zahl wert={euroWert(b.vkNettoCents)} art="euro" /></strong>
      <small className="kalk-block__fuss">
        <span>EK <Zahl wert={euroWert(b.ekNettoCents)} art="euro" /></span>
        <span className={b.gewinnNettoCents >= 0 ? "kalk-gut" : "kalk-schlecht"}>Gewinn <Zahl wert={euroWert(b.gewinnNettoCents)} art="euro" /></span>
      </small>
    </div>
  );
}

function Gruppen({ gruppen }: { gruppen: ObjektGruppe[] }) {
  if (gruppen.length === 0) return <p className="muted small">Keine Zuordnung.</p>;
  return (
    <ul className="kalk-gruppen">
      {gruppen.map((g) => (
        <li key={g.bezeichnung}>
          <span>{g.bezeichnung} <small className="muted">{g.positionen.length} Pos.</small></span>
          <strong><Zahl wert={euroWert(g.vkNettoCents)} art="euro" /> <small className={`muted ${g.gewinnNettoCents >= 0 ? "kalk-gut" : "kalk-schlecht"}`}>+<Zahl wert={euroWert(g.gewinnNettoCents)} art="euro" /></small></strong>
        </li>
      ))}
    </ul>
  );
}

function LohntSich({ wert, gewinnCents, sollCents }: { wert: number; gewinnCents: number; sollCents: number }) {
  const anteil = useFeder(Math.min(1.5, Math.max(0, wert)) / 1.5, { response: 0.5 });
  const ton = wert >= 1 ? "gut" : wert >= 0.6 ? "warnung" : "schlecht";
  return (
    <div className="kalk-lohnt">
      <div className="kalk-lohnt__kopf">
        <span>Gewinn je Arbeitsstunde</span>
        <strong><Zahl wert={euroWert(gewinnCents)} art="euro" /> <span className="muted">/ Soll <Zahl wert={euroWert(sollCents)} art="euro" /></span></strong>
      </div>
      <div className="kalk-lohnt__spur" role="meter" aria-valuenow={Math.round(wert * 100)} aria-valuemin={0} aria-valuemax={150} aria-label="Quotient Ist zu Soll">
        <span className={`kalk-lohnt__balken kalk-lohnt__balken--${ton}`} style={{ transform: `scaleX(${anteil})` }} />
        <span className="kalk-lohnt__soll" style={{ left: `${(1 / 1.5) * 100}%` }} />
      </div>
    </div>
  );
}
